import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { Popup, Divider, Rate } from 'antd-mobile'
import { saveRPE, saveEnjoyScore } from '../../../api/program'
import HeartIcon from '../../../asserts/icons/physical-assessment/heart_icon.svg'
import '../styles/EditRecommendationModal.css'
import '../styles/ProgramModal.css'
import HeartRateZones from '../Charts/HeartRateZones'
import MobileActivityTrend from '../Charts/MobileActivityTrend'
import RPESelector from '../Charts/RPESelector'
import WeekDatePicker from '../Charts/WeekDatePicker'
import { formatLabel } from '../../../utils'

function ProgramWorkoutDetailModal({ visible, onClose, workoutDetail, readOnly, defaultSelectedDateIndex, onUpdated  }) {
  const secondsToMinutes = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    return seconds % 60 < 30 ? minutes : minutes + 1
  }
  const [rpeValue, setRpeValue] = useState(1)
  const [enjoyScore, setEnjoyScore] = useState(workoutDetail?.enjoy_score || null)
  const [selectedDateIndex, setSelectedDateIndex] = useState(
    moment().isoWeekday() - 1
  )
  const durationInMinutes = secondsToMinutes(workoutDetail?.details?.duration || 0)
  const trainingLoad = Math.round(rpeValue * durationInMinutes)
  const workoutId = workoutDetail?.id
  const isManualWorkout = workoutDetail?.manual

  useEffect(() => {
    if (visible && typeof defaultSelectedDateIndex === 'number') {
      setSelectedDateIndex(defaultSelectedDateIndex)
    }
  }, [visible, defaultSelectedDateIndex])  

  useEffect(() => {
    if (workoutDetail) {
      const manual = workoutDetail.rpe_manual
      const auto = workoutDetail.rpe_auto
      const value = manual ?? auto ?? 1
      setRpeValue(value)
    }
  }, [workoutDetail?.rpe_manual, workoutDetail?.rpe_auto])  

  useEffect(() => {
    if (workoutDetail?.enjoy_score) {
      setEnjoyScore(workoutDetail.enjoy_score)
    }
  }, [workoutDetail?.enjoy_score])

  const plannedDuration = workoutDetail?.planned_duration || '-'
  const plannedIntensity = workoutDetail?.planned_intensity || '--'
  const actualDuration = workoutDetail?.details?.formattedDuration || '--'
  const actualIntensity = workoutDetail?.actual_intensity || '--'

  const handleRPEChange = (val) => {
    setRpeValue(val)
  }

  const handleRPECommit = async (val) => {
    const workoutId = workoutDetail.id
    try {
      await saveRPE(workoutId, val)
      onUpdated?.()
    } catch (error) {
      console.error('Failed to save RPE:', error)
    }
  }

  const handleEnjoyScoreChange = async (val) => {
    setEnjoyScore(val)

    try {
      await saveEnjoyScore(workoutId, val)
    } catch (error) {
      console.error('Failed to save enjoy score:', error)
    }
  }

  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      onClose={onClose}
      position="bottom"
      bodyClassName="modal-popup"
    >
      <div className="modal-content">
        <div className="modal-header-wrapper">
          <div className="modal-header">
            <h2>Exercise Details</h2>
            <button
              onClick={onClose}
              className="modal-close-btn"
              aria-label="Close"
            >
              Close
            </button>
          </div>
          <WeekDatePicker
            selectedDateIndex={selectedDateIndex}
            onSelectDate={(index) => setSelectedDateIndex(index)}
            readOnly={readOnly}
          />
        </div>

        <div className="workout-compare-wrapper">
          <div className="workout-compare-detail-container">
            <div className="planned-data-container">
              <span className="workout-field-label">Planned</span>
              <p className="workout-field-value" style={{ fontSize: '16px' }}>
                {plannedDuration}
              </p>
              <p className="workout-field-value" style={{ fontSize: '16px' }}>
                {plannedIntensity}
              </p>
            </div>
            <div className="vs-container">
              <span className="vs-label vs-text">vs</span>
              <span className="vs-label">Duration</span>
              <span className="vs-label">Intensity</span>
            </div>
            <div className="actual-data-container">
              <span className="workout-field-label">Completed</span>
              <p className="workout-field-value" style={{ fontSize: '16px' }}>
                {actualDuration}
              </p>
              <p className="workout-field-value" style={{ fontSize: '16px' }}>
                {actualIntensity}
              </p>
            </div>
          </div>
          <Divider style={{ margin: '6px 0px 15px 0px' }} />

          <div className="enjoy-score-container">
            <span className="workout-field-label">
              Did you enjoy your workout?
            </span>
            <div className="enjoy-rate-wrapper">
              <Rate
                count={5}
                value={enjoyScore}
                onChange={handleEnjoyScoreChange}
                style={{
                  '--active-color': 'rgb(255, 107, 0)'
                }}
              />
            </div>
          </div>
        </div>
        <div className="workout-details-container">
          <div className="workout-field">
            <span className="workout-field-label">Workout Name</span>
            <span className="workout-field-value">
              {formatLabel(
                workoutDetail?.details?.activity_type_shown ??
                workoutDetail?.details?.activity_type
              )}
            </span>

          </div>

          <div className="workout-field-row">
            <div className="workout-field">
              <span className="workout-field-label">Source</span>
              <span
                className="workout-field-value"
                style={{ textTransform: 'capitalize' }}
              >
                {workoutDetail?.details?.source ?? '--'}
              </span>
            </div>
            <div className="workout-field">
              <span className="workout-field-label">Type</span>
              <span className="workout-field-value">
                {workoutDetail?.details?.activity_classification ? (
                  <>
                    <span className={`circle ${workoutDetail.details.activity_classification}`}></span>
                    {workoutDetail.details.activity_classification}
                  </>
                ) : workoutDetail?.type ? (
                  <>
                    <span className={`circle ${workoutDetail.type}`}></span>
                    {workoutDetail.type}
                  </>
                ) : (
                  '--'
                )}
              </span>
            </div>

          </div>

          {isManualWorkout ? (
            // Manual workout: duration in its own row
            <div className="workout-field-row">
              <div className="workout-field">
                <span className="workout-field-label">Duration</span>
                <span className="workout-field-value">
                  {workoutDetail?.details?.formattedDuration ?? '--'}
                </span>
              </div>
            </div>
          ) : (
            // Non-manual: duration + calories in same row
            <div className="workout-field-row">
              <div className="workout-field">
                <span className="workout-field-label">Duration</span>
                <span className="workout-field-value">
                  {workoutDetail?.details?.formattedDuration ?? '--'}
                </span>
              </div>
              <div className="workout-field">
                <span className="workout-field-label">Active Calories</span>
                <span className="workout-field-value">
                    {workoutDetail?.details?.energy_burned != null
                      ? `${workoutDetail?.details?.energy_burned} Cal`
                      : '--'}
                </span>
              </div>
            </div>
          )}

          {!isManualWorkout && (
            <div className="workout-field-row">
              <div className="workout-field">
                <span className="workout-field-label">Max Heart Rate</span>
                <span className="workout-field-value">
                  {workoutDetail?.details?.max_heart_rate != null
                    ? `${workoutDetail?.details?.max_heart_rate} bpm`
                    : '--'}
                </span>
              </div>
              <div className="workout-field">
                <span className="workout-field-label">Avg Heart Rate</span>
                <span className="workout-field-value">
                  {workoutDetail?.details?.average_heart_rate != null
                    ? `${workoutDetail?.details?.average_heart_rate} bpm`
                    : '--'}
                </span>
              </div>
            </div>
          )}

          {workoutDetail?.details?.distance > 0 && (
            <div className="workout-field">
              <span className="workout-field-label">Distance</span>
              <span className="workout-field-value">
                {workoutDetail?.details?.distance != null
                  ? `${(workoutDetail?.details?.distance * 0.000621371).toFixed(2)} mi`
                  : '--'}
              </span>
            </div>
          )}

        </div>

        {!isManualWorkout && (
          <div className="heart-rate-zone-container">
            <HeartRateZones activityDetail={workoutDetail?.details} />
          </div>
        )}

        {!isManualWorkout && (
          <div className="heart-rate-trend-container">
            <div className="heart-rate-title" style={{ marginTop: '16px'}}>
              <img src={HeartIcon} alt="Heart" />
              <span>Heart Rate</span>
            </div>
            <MobileActivityTrend detail={workoutDetail?.details} />
          </div>
        )}
        <div className="rpe-trend-container">
          <RPESelector
            rpeValue={rpeValue}
            onChange={handleRPEChange}
            onCommit={handleRPECommit}
            trainingLoad={trainingLoad}
          />
        </div>

      </div>
    </Popup>
  )
}

export default ProgramWorkoutDetailModal
